import { CascaPainel, Carregando, Cartao, FaixaAmbiente, Faixas, FaixaVersaoNova, SinoDaEquipe, type EntradaDeMenu } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { useApi, type Capacidades } from "./api";
import { PaginaNotificacoes, type SinoDaApi } from "./paginas/Notificacoes";
import { config, marca } from "./config";
import { useVersaoDoServidor } from "./versao";
import { AmbienteCtx, type Contexto } from "./contexto";
import { PaginaEntrar, PaginaEsqueciSenha } from "./paginas/Acesso";
import { PaginaAcademy } from "./paginas/Academy";
import { PaginaAdministradores } from "./paginas/Administradores";
import { PaginaBeneficios } from "./paginas/BeneficiosDaOferta";
import { PaginaComprovantes } from "./paginas/Comprovantes";
import { PaginaComunicacao, PaginaComunicacoes } from "./paginas/Comunicacoes";
import { PaginaContasNaCorretora } from "./paginas/ContasNaCorretora";
import { PaginaConfiguracaoDaFunded } from "./paginas/ConfiguracaoDaFunded";
import { PaginaCupons } from "./paginas/Cupons";
import { PaginaFerramentas } from "./paginas/Ferramentas";
import { PaginaImportacaoGlobal } from "./paginas/ImportacaoGlobal";
import { PaginaInscricoes } from "./paginas/Inscricoes";
import { PaginaMatricula } from "./paginas/Matriculas";
import { PaginaMensalidades } from "./paginas/Mensalidades";
import { PaginaPlanos } from "./paginas/Planos";
import { PaginaPromocoes } from "./paginas/Promocoes";
import { PaginaTrader, PaginaTraders } from "./paginas/Traders";
import { PaginaSolicitacao, PaginaSolicitacoes } from "./paginas/Solicitacoes";
import { PaginaRegulamentos } from "./paginas/Regulamentos";
import { PaginaLembretes } from "./paginas/Lembretes";
import { PaginaRepasses } from "./paginas/Repasses";
import { PaginaRepassesAntigos } from "./paginas/RepassesAntigos";
import { PaginaAdicionarPlanosAntigos, PaginaPlanosAntigos } from "./paginas/PlanosAntigos";
import { useSomAtivo, useSomDoSino } from "./som";
import { PaginaCheckouts, PaginaVendas } from "./paginas/Vendas";
import { PaginaVendasAntigas } from "./paginas/VendasAntigas";
import { VisaoGeral } from "./paginas/VisaoGeral";
import { PaginaVisaoDoCliente } from "./paginas/VisaoDoCliente";
import { PaginaAnalise } from "./paginas/Analise";
import { usePapel } from "./api";
import { PAPEL, useSessao } from "./sessao";

type Grupo = "Financeiro" | "Comercial" | "Operação" | "Cadastros";
type Papel = "OWNER" | "ADMIN" | "SUPORTE" | "ANALISTA";
type Entrada = { modulos?: string[]; rota: string; rotulo: string; tela?: ReactNode; so?: (c: Contexto) => boolean; grupo?: Grupo; papel?: Papel };

/**
 * Menu por capacidade do ambiente: sem o modulo, sem a entrada. Nenhum `if` de marca. Os grupos (Vitor, 15/09:
 * Financeiro, Comercial e Cadastros) viram acordeao na lateral, na posicao da primeira entrada de cada um.
 */
const ENTRADAS: Entrada[] = [
  { rota: "/", rotulo: "Visão geral" },
  // Na Global (spec 064) o nelogica-v2 abre o pedido de aprovacao do Challenge e a liberacao da Funded.
  { modulos: ["agendamento", "etapas", "nelogica-v2"], rota: "/solicitacoes", rotulo: "Solicitações", tela: <PaginaSolicitacoes /> },
  // A virada da Prop (spec 009, secao 4): a fila das contas ativas na Nelogica, conferidas uma por uma.
  // A virada da Global (spec 040): os planos do sistema antigo, por cliente, cada um aprovado ou marcado que nao vem.
  { modulos: ["nelogica-v2"], rota: "/importacao-global", rotulo: "Importação", tela: <PaginaImportacaoGlobal /> },
  // Vendas pelo checkout (spec 003): o painel fala direto com o servico de pagamentos do ambiente.
  { grupo: "Financeiro", rota: "/checkouts", rotulo: "Checkouts", tela: <PaginaCheckouts />, so: () => Boolean(config().PAGAMENTOS_URL) },
  { grupo: "Financeiro", modulos: ["mensalidade-manual"], rota: "/mensalidades", rotulo: "Mensalidades", tela: <PaginaMensalidades /> },
  // A taxa de manutencao da Funded (spec 064): a mesma tela e as mesmas rotas, com o nome que a Global usa.
  { grupo: "Financeiro", modulos: ["assinatura"], rota: "/mensalidades", rotulo: "Taxa de manutenção", tela: <PaginaMensalidades /> },
  // Comprovantes que o cliente manda pela area (spec 039): so ha o que mandar onde ha mensalidade ou avanco pago. Na
  // Global, a taxa de manutencao e a taxa de ativacao do Challenge (spec 064).
  { grupo: "Financeiro", modulos: ["mensalidade-manual", "etapas", "assinatura", "nelogica-v2"], rota: "/comprovantes", rotulo: "Comprovantes", tela: <PaginaComprovantes /> },
  // Repasses da Mesa Real (spec 013, item 1): a conferencia do periodo; registrar e na ficha da inscricao.
  { grupo: "Financeiro", modulos: ["etapas"], rota: "/repasses", rotulo: "Repasses", tela: <PaginaRepasses /> },
  // Os repasses que vieram do sistema antigo, numa planilha pra acertar sem avisar o cliente (spec 054, 4).
  { grupo: "Financeiro", modulos: ["etapas"], rota: "/repasses-antigos", rotulo: "Repasses antigos", tela: <PaginaRepassesAntigos /> },
  { grupo: "Financeiro", modulos: ["assinatura"], rota: "/assinaturas", rotulo: "Assinaturas" },
  { grupo: "Financeiro", modulos: ["saldo"], rota: "/saldo", rotulo: "Saldo" },
  { grupo: "Comercial", rota: "/vendas", rotulo: "Vendas", tela: <PaginaVendas />, so: () => Boolean(config().PAGAMENTOS_URL) },
  // O funil do checkout e por que as pessoas nao compram (spec 062): todo administrador ve (ADR-011).
  { grupo: "Comercial", rota: "/analise", rotulo: "Análise", tela: <PaginaAnalise />, so: () => Boolean(config().PAGAMENTOS_URL) },
  // Promocoes (spec 067; Vitor, 02/11: "pode colocar na lista de comercial dos links no painel"): o preco com desconto
  // no checkout e nos planos a venda, sem codigo. Todo administrador ve; criar e mudar e do ADMIN, como no cupom.
  { grupo: "Comercial", rota: "/promocoes", rotulo: "Promoções", tela: <PaginaPromocoes />, so: () => Boolean(config().PAGAMENTOS_URL) },
  // Venda do sistema antigo e dado pessoal de quem nem esta mais ativo: so de SUPORTE pra cima (Vitor, 15/09).
  { grupo: "Comercial", rota: "/vendas-antigas", rotulo: "Vendas antigas", tela: <PaginaVendasAntigas />, so: () => Boolean(config().PAGAMENTOS_URL), papel: "SUPORTE" },
  // Os textos dos e-mails do sistema (spec 041, 3.8): o backend repassa pro servico de e-mails do ambiente.
  { grupo: "Comercial", rota: "/comunicacoes", rotulo: "E-mails", tela: <PaginaComunicacoes /> },
  // O lembrete pos-repasse (spec 048): zerar o Monitor de Risco e reinserir o perfil de risco ate as 09:00.
  { grupo: "Operação", modulos: ["etapas"], rota: "/lembretes", rotulo: "Lembretes", tela: <PaginaLembretes /> },
  // O que a area do cliente mostra, pra todos os clientes (spec 060): liga e desliga cada parte, com trava de tempo.
  { grupo: "Operação", rota: "/visao-do-cliente", rotulo: "Visão do cliente", tela: <PaginaVisaoDoCliente /> },
  { grupo: "Cadastros", rota: "/planos", rotulo: "Planos", tela: <PaginaPlanos /> },
  // O valor e os prazos da Funded da Global (spec 065): a cobranca da taxa de manutencao (assinatura) e os prazos do
  // pedido de aprovacao (nelogica-v2). Ao lado de Planos, onde fica o valor de cada plano.
  { grupo: "Cadastros", modulos: ["assinatura", "nelogica-v2"], rota: "/configuracao-da-funded", rotulo: "Configuração da Funded", tela: <PaginaConfiguracaoDaFunded /> },
  // Beneficios que os checkouts oferecem como order bump (spec 013, item 2; Vitor, 16/09: "numa tela chamada de beneficios dentro de cadastros").
  { grupo: "Cadastros", rota: "/beneficios", rotulo: "Benefícios", tela: <PaginaBeneficios />, so: () => Boolean(config().PAGAMENTOS_URL) },
  // Cupons de desconto do checkout (spec 016).
  { grupo: "Cadastros", rota: "/cupons", rotulo: "Cupons", tela: <PaginaCupons />, so: () => Boolean(config().PAGAMENTOS_URL) },
  // O que cada compra aceita (specs 017 e 042): a versao atual, as anteriores e publicar versao nova, sem deploy.
  { grupo: "Cadastros", rota: "/regulamentos", rotulo: "Regulamentos", tela: <PaginaRegulamentos /> },
  // Academy (spec 011): o conteudo da area do trader e a fila de perguntas das aulas.
  { grupo: "Cadastros", rota: "/academy", rotulo: "Academy", tela: <PaginaAcademy /> },
  // Spec 030: a entrada existia sem `tela` desde sempre, entao nunca apareceu no menu.
  { grupo: "Cadastros", rota: "/traders", rotulo: "Clientes", tela: <PaginaTraders /> },
  { grupo: "Cadastros", modulos: ["etapas"], rota: "/planos-antigos", rotulo: "Planos antigos", tela: <PaginaPlanosAntigos /> },
  // Cada plano de cada trader (as antigas Matriculas): ativas em dia ou com pendencia, e as outras situacoes.
  { grupo: "Cadastros", rota: "/inscricoes", rotulo: "Inscrições", tela: <PaginaInscricoes /> },
  // Spec 043: as contas dos planos da Global com o estado e o saldo que a corretora diz (so leitura).
  { grupo: "Cadastros", modulos: ["nelogica-v2"], rota: "/contas-na-corretora", rotulo: "Contas na corretora", tela: <PaginaContasNaCorretora /> },
  // Quem entra no painel (Vitor, 15/10): as rotas sao so do dono da conta, entao a tela tambem.
  { grupo: "Cadastros", rota: "/administradores", rotulo: "Administradores", tela: <PaginaAdministradores />, papel: "OWNER" },
  { rota: "/ferramentas", rotulo: "Ferramentas de teste", tela: <PaginaFerramentas />, so: (c) => c.ferramentasDeTeste },
];

/** As entradas visiveis viram o menu: soltas ou dentro do grupo delas, na ordem da primeira de cada grupo. */
function montarMenu(visiveis: Entrada[], ativo: (rota: string) => boolean): EntradaDeMenu[] {
  const menu: EntradaDeMenu[] = [];
  const grupos = new Map<Grupo, { rotulo: string; itens: { rotulo: string; href: string; atual: boolean }[] }>();
  for (const e of visiveis) {
    const item = { rotulo: e.rotulo, href: e.rota, atual: ativo(e.rota) };
    if (!e.grupo) {
      menu.push(item);
      continue;
    }
    let g = grupos.get(e.grupo);
    if (!g) {
      g = { rotulo: e.grupo, itens: [] };
      grupos.set(e.grupo, g);
      menu.push(g);
    }
    g.itens.push(item);
  }
  return menu;
}

function Protegido({ children }: { children: ReactNode }) {
  const { estado } = useSessao();
  const local = useLocation();
  if (estado.status === "carregando") return <Carregando texto="Conferindo sua sessão" />;
  if (estado.status === "anonimo") return <Navigate to={`/entrar?volta=${encodeURIComponent(local.pathname + local.search)}`} replace />;
  return <>{children}</>;
}

function SoAnonimo({ children }: { children: ReactNode }) {
  const { estado } = useSessao();
  if (estado.status === "carregando") return <Carregando texto="Conferindo sua sessão" />;
  if (estado.status === "logado") return <Navigate to="/" replace />;
  return <>{children}</>;
}

function EmConstrucao({ titulo }: { titulo: string }) {
  return (
    <div style={{ display: "grid", gap: "var(--e-4)" }}>
      <h1 className="mtc-titulo mtc-cabecalho__titulo">{titulo}</h1>
      <Cartao><p className="mtc-texto-2" style={{ margin: 0 }}>Esta tela ainda está em construção.</p></Cartao>
    </div>
  );
}

/**
 * Sino da equipe (spec 010): alertas e pendencias abertos (resolvidos, saem pra todos) e avisos com o "novo" de cada
 * operador. Mudou a contagem, as filas da tela se atualizam.
 */
function SinoDoPainel() {
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const navegar = useNavigate();
  // Pergunta tambem com a aba em segundo plano (spec 048, 2.10): o som e justamente pra quem esta em outra aba.
  const sino = useQuery({ queryKey: ["avisos"], queryFn: () => ler<SinoDaApi>("/v1/admin/notificacoes/sino"), refetchInterval: 20_000, refetchIntervalInBackground: true });
  const itens = useMemo(() => (sino.data ? [...sino.data.alertas, ...sino.data.pendencias, ...sino.data.avisos] : undefined), [sino.data]);
  useSomDoSino(itens);
  const somAtivo = useSomAtivo();
  const c = sino.data?.contagem ?? { pendencias: 0, alertas: 0, avisos_novos: 0, novas: 0 };
  const total = c.pendencias + c.alertas + c.avisos_novos;
  const antes = useRef<number | null>(null);
  useEffect(() => {
    if (antes.current !== null && total > antes.current) void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "avisos" });
    antes.current = total;
  }, [total, qc]);
  const recarregar = () => Promise.all([qc.invalidateQueries({ queryKey: ["avisos"] }), qc.invalidateQueries({ queryKey: ["notificacoes"] })]);
  return (
    <>
    {/* O navegador so toca depois do primeiro clique na pagina: ate la, o convite (qualquer clique ja liga). */}
    {!somAtivo && <button type="button" className="mtc-link" style={{ fontSize: "var(--t-xs)" }} title="O sino toca nas solicitações e nos repasses">Ativar som</button>}
    <SinoDaEquipe
      lado="esquerda"
      contagem={c}
      alertas={sino.data?.alertas ?? []}
      pendencias={sino.data?.pendencias ?? []}
      avisos={sino.data?.avisos ?? []}
      carregando={sino.isLoading}
      verTodasHref="/notificacoes"
      // Busca o que tem agora (com o "novo" de cada item) e so depois marca o sino como visto: a bolinha dos novos fica
      // ate a proxima atualizacao, e o numero some na hora (Vitor, 06/10: "so o que e novo pra voce").
      aoAbrir={() => void sino.refetch().finally(() => void postar("/v1/admin/notificacoes/sino/visto").catch(() => undefined))}
      aoAbrirItem={(n) => { if (n.link?.startsWith("/")) navegar(n.link); }}
      aoResolver={async (n, observacao) => {
        try {
          await postar(`/v1/admin/notificacoes/${n.id}/resolver`, observacao ? { observacao } : {});
          await recarregar();
          return true;
        } catch {
          return false;
        }
      }}
      // O "novo" some na proxima atualizacao do sino: da tempo de ver o que chegou.
      aoVerAvisos={() => void postar("/v1/admin/notificacoes/avisos/vistos").catch(() => undefined)}
    />
    </>
  );
}

function PainelLogado() {
  const { estado, sair } = useSessao();
  const { ler } = useApi();
  const local = useLocation();
  const capacidades = useQuery({ queryKey: ["capacidades"], queryFn: () => ler<Capacidades>("/v1/admin/capacidades") });
  const modulos = capacidades.data?.modulos ?? [];
  const ambiente = capacidades.data?.ambiente ?? config().AMBIENTE ?? "local";
  const papel = estado.status === "logado" ? estado.usuario.role : "ANALISTA";
  const ctx: Contexto = {
    ambiente,
    modulos,
    // Simular compra e relogio: so fora de producao, ADMIN pra cima (o backend recusa o resto).
    ferramentasDeTeste: ambiente !== "producao" && (papel === "ADMIN" || papel === "OWNER"),
  };
  const { pode } = usePapel();
  // Entrada sem tela nao aparece (spec 013, item 8): o menu so mostra o que abre de verdade. "/" e a visao geral.
  const visiveis = ENTRADAS.filter((e) => (e.rota === "/" || e.tela) && (!e.modulos || e.modulos.some((m) => modulos.includes(m))) && (!e.so || e.so(ctx)) && (!e.papel || pode(e.papel)));
  const board = config().EMAILS_BOARD_URL;
  const ativo = (rota: string) => (rota === "/" ? local.pathname === "/" : local.pathname === rota || local.pathname.startsWith(`${rota}/`));
  return (
    <AmbienteCtx.Provider value={ctx}>
      <CascaPainel
        marca={marca()}
        usuario={estado.status === "logado" ? { nome: estado.usuario.nome, papel: PAPEL[estado.usuario.role] } : undefined}
        aoSair={() => void sair()}
        acoes={<SinoDoPainel />}
        itens={montarMenu(visiveis, ativo)}
        extras={board ? <a href={board} target="_blank" rel="noreferrer" className="mtc-rotulo" style={{ padding: "0 var(--e-3)" }}>Fila de e-mails</a> : undefined}
      >
        <Routes>
          <Route path="/" element={<VisaoGeral modulos={modulos} carregando={capacidades.isLoading} />} />
          {visiveis.some((e) => e.rota === "/solicitacoes") && <Route path="/solicitacoes/:id" element={<PaginaSolicitacao />} />}
          <Route path="/matriculas/:id" element={<PaginaMatricula />} />
          <Route path="/traders/:id" element={<PaginaTrader />} />
          {visiveis.some((e) => e.rota === "/planos-antigos") && <Route path="/traders/:id/planos-antigos" element={<PaginaAdicionarPlanosAntigos />} />}
          {/* A lista de matriculas virou Inscricoes (15/09): link antigo cai na aba Todas. */}
          <Route path="/matriculas" element={<Navigate to="/inscricoes?aba=todas" replace />} />
          <Route path="/notificacoes" element={<PaginaNotificacoes />} />
          {visiveis.some((e) => e.rota === "/comunicacoes") && <Route path="/comunicacoes/:chave" element={<PaginaComunicacao />} />}
          <Route path="/planos/:slug" element={<PaginaPlanos />} />
          {visiveis.filter((e) => e.rota !== "/").map((e) => <Route key={e.rota} path={e.rota} element={e.tela ?? <EmConstrucao titulo={e.rotulo} />} />)}
          <Route path="*" element={capacidades.isLoading ? <Carregando compacto /> : <EmConstrucao titulo="Esta tela não existe neste ambiente" />} />
        </Routes>
      </CascaPainel>
    </AmbienteCtx.Provider>
  );
}

export function App() {
  const versao = useVersaoDoServidor();
  return (
    <>
      <Faixas>
        {config().AMBIENTE === "homologacao" && <FaixaAmbiente />}
        {versao.desatualizada && <FaixaVersaoNova aoAtualizar={() => window.location.reload()} />}
      </Faixas>
      <Routes>
        <Route path="/entrar" element={<SoAnonimo><PaginaEntrar /></SoAnonimo>} />
        <Route path="/esqueci-senha" element={<SoAnonimo><PaginaEsqueciSenha /></SoAnonimo>} />
        <Route path="/*" element={<Protegido><PainelLogado /></Protegido>} />
      </Routes>
    </>
  );
}
